// Steuerpaket S4 im Eigentümerportal: vom Büro freigegebene Jahre als PDF. Zahlen nur aus dem freigegebenen Stand.
import { useEffect, useState } from 'react';
import { FilePdf, Receipt } from '@phosphor-icons/react';
import { GlasWidget } from './bausteine';
import { ladeMeineSteuerpakete, type FreigabeZeile } from '../../lib/buchhaltung/api';
import { ladeBriefpapier } from '../../lib/dokumente/blankoBrief';
import { erzeugeSteuerpaketPdf, steuerpaketDateiname } from '../../lib/buchhaltung/steuerpaketPdf';
import { dateiname35a, erzeuge35aPdf, type Bescheinigung35a } from '../../lib/buchhaltung/bescheinigung35a';

const ZEILE = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, background: 'var(--p-w05)', minWidth: 0, flexWrap: 'wrap' } as const;
const euro = (n: number) => `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export function SteuerpaketWidget() {
  const [pakete, setPakete] = useState<FreigabeZeile[] | null>(null);
  const [laeuft, setLaeuft] = useState<number | null>(null);
  useEffect(() => { void ladeMeineSteuerpakete().then(setPakete, () => setPakete([])); }, []);
  if (!pakete?.length) return null;
  // #584: hängen mehrere Eigentümer-Datensätze am Zugang, steht der Name am Jahr
  const mitName = new Set(pakete.map((p) => p.stand.eigentuemer)).size > 1;
  const lade35a = async (b: Bescheinigung35a) => {
    const papier = await ladeBriefpapier().catch(() => null);
    erzeuge35aPdf(b, papier).save(dateiname35a(b));
  };
  const lade = async (p: FreigabeZeile) => {
    setLaeuft(p.jahr);
    try { const papier = await ladeBriefpapier().catch(() => null); erzeugeSteuerpaketPdf(p.stand, papier).save(steuerpaketDateiname(p.stand)); }
    finally { setLaeuft(null); }
  };
  return (
    <GlasWidget farbe="konto" icon={Receipt} titel="Steuerpaket">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {pakete.map((p) => {
          const ein = p.stand.objekte.reduce((s, o) => s + o.summeEinnahmen, 0);
          const wk = p.stand.objekte.reduce((s, o) => s + o.summeWerbungskosten, 0);
          return (
            <div key={`${p.jahr}-${p.stand.eigentuemer}`} style={ZEILE}>
              <span style={{ fontSize: 15, fontWeight: 800, minWidth: 48 }}>{p.jahr}</span>
              {mitName && <span className="p-kuerzen" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-gold-text)', maxWidth: 200 }}>{p.stand.eigentuemer}</span>}
              <span style={{ flex: 1, minWidth: 160, fontSize: 12.5, fontWeight: 600, color: 'var(--p-w55)' }}>
                {p.stand.objekte.length ? `${ein ? `Einnahmen ${euro(ein)} · ` : ''}Werbungskosten ${euro(wk)}` : 'Steuerbescheinigung'}
              </span>
              {p.stand.objekte.length > 0 && (
                <button type="button" className="p-knopf" disabled={laeuft === p.jahr} onClick={() => void lade(p)} data-tip="Aufstellung als PDF herunterladen">
                  <FilePdf size={16} weight="bold" /> PDF
                </button>
              )}
              {(p.stand.paragraph35a ?? []).map((b) => (
                <button key={b.objekt} type="button" className="p-knopf" onClick={() => void lade35a(b)} data-tip={`§ 35a-Bescheinigung ${b.objekt} herunterladen`}>
                  <FilePdf size={16} weight="bold" /> § 35a{(p.stand.paragraph35a?.length ?? 0) > 1 ? ` ${b.objekt}` : ''}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-w45)' }}>Zusammenstellung aus der Buchhaltung, keine Steuerberatung.</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w45)' }}>AfA, eigene Darlehen, Fahrtkosten und sonstige eigene Ausgaben ergänzen Sie bzw. Ihr Steuerberater.</span>
    </GlasWidget>
  );
}
